import { cn } from '../lib/cn'

/**
 * A placeholder in the shape of the thing that is loading.
 *
 * <p>The product said "Chargement…" and showed an empty screen. A blank page
 * with one grey word tells you nothing is happening; a placeholder in the shape
 * of the list you asked for tells you it is on its way and roughly how much of
 * it there is. On a mobile network in Benin that difference is most of the
 * waiting experience.
 *
 * <p>Sweeps rather than pulses. At this size a pulse reads as something
 * failing; a sweep reads as something arriving — and it runs once per 1.4s
 * rather than throbbing, so a screenful of them is not a strobe.
 *
 * <p>`aria-hidden`, always. A screen reader gets the `role="status"` message
 * from whatever is rendering these; announcing nine grey boxes is noise.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'animate-shimmer block rounded-md bg-slate-200/70',
        'bg-[linear-gradient(90deg,transparent_0%,--theme(--color-slate-100)_50%,transparent_100%)]',
        'bg-[length:200%_100%]',
        className,
      )}
    />
  )
}

/**
 * A stack of rows, for a list that has not arrived.
 *
 * <p>Widths vary down the stack because real text does: a column of
 * identical bars looks like a loading *graphic*, and a ragged one looks like
 * text about to resolve.
 */
export function SkeletonList({
  rows = 5,
  className,
  avatar = false,
}: {
  rows?: number
  className?: string
  /** Leads each row with a circle, for lists of people or providers. */
  avatar?: boolean
}) {
  const widths = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-1/2', 'w-3/5']
  return (
    <div className={cn('flex flex-col gap-4', className)} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          {avatar ? <Skeleton className="size-10 shrink-0 rounded-full" /> : null}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className={cn('h-3.5', widths[i % widths.length])} />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  )
}
